import {
  describeRecordingFailure,
  type LiveRecordingConfig,
  type RecordedChunk,
  type RecorderAdapter,
  type RecordingFailure,
  type RecordingSource,
  type RecordingState,
} from './LiveRecording.js';

/**
 * One live recording, from arming to a finished file.
 *
 * All of the state machine and none of the platform. `MediaRecorder`,
 * `captureStream` and the audio graph live in `src/view/record/`; what is here
 * is the part that decides *when* things may happen, which is the part that goes
 * wrong and the part worth testing without a browser.
 *
 * ## Why the states are explicit
 *
 * Recording has a shape most features do not: it is started by one thing (a
 * button, or the run beginning), stopped by another (a different button, or the
 * song ending), and finished by a third — the browser, whenever it feels like
 * delivering the last chunk. Those can interleave. A player can press STOP while
 * the song is ending; a run can start while a manual recording is already
 * running; a stop can arrive after a cancel.
 *
 * Tracking that with a pair of booleans produces two recorders writing one file,
 * or a `DONE` that arrives before the data does. So the states are named, the
 * transitions are checked, and every entry point returns without doing anything
 * when it is called from a state that does not allow it.
 *
 * ## Why the final chunk is waited for
 *
 * `stop()` does not finish a recording — it asks the host to finish one. The
 * last chunk arrives afterwards, and disposing before it lands truncates the
 * file by however much was still buffered. That is why `STOPPING` exists as a
 * state rather than being folded into `DONE`: something has to represent "asked
 * to stop, not yet finished", and the download button must not appear during it.
 */

export interface LiveRecordingResult {
  readonly chunks: readonly RecordedChunk[];
  readonly bytes: number;
  readonly mimeType: string;
  readonly extension: string;
  readonly durationSeconds: number;
  readonly width: number;
  readonly height: number;
  readonly fps: number;
  readonly hasAudio: boolean;
}

export interface LiveRecordingStatus {
  readonly state: RecordingState;
  readonly elapsedSeconds: number;
  readonly chunks: number;
  readonly bytes: number;
  readonly mimeType: string;
  readonly hasAudio: boolean;
  /** True while the run, rather than a button, owns this recording. */
  readonly automatic: boolean;
  readonly error: string | null;
}

export interface LiveRecordingOptions {
  readonly recorder: RecorderAdapter;
  readonly source: RecordingSource;
  readonly config: LiveRecordingConfig;
  readonly extension: string;
  /** Milliseconds between chunks. See `start`. */
  readonly timesliceMs?: number;
  readonly now?: () => number;
  readonly onStatus?: (status: LiveRecordingStatus) => void;
}

export class LiveRecordingSession {
  private readonly recorder: RecorderAdapter;
  private readonly source: RecordingSource;
  private readonly config: LiveRecordingConfig;
  private readonly extension: string;
  private readonly timesliceMs: number;
  private readonly now: () => number;
  private readonly onStatus: ((status: LiveRecordingStatus) => void) | undefined;

  private state: RecordingState = 'IDLE';
  private readonly chunks: RecordedChunk[] = [];
  private byteCount = 0;
  private startedAt = 0;
  private stoppedAt = 0;
  private error: string | null = null;
  private automatic = false;
  /** Set when a cancel arrives, so the host's stop callback discards instead. */
  private cancelled = false;
  private released = false;

  constructor(options: LiveRecordingOptions) {
    this.recorder = options.recorder;
    this.source = options.source;
    this.config = options.config;
    this.extension = options.extension;
    // A timeslice, so data arrives during the recording rather than as one
    // enormous blob at the end. Without it a crash mid-run loses everything,
    // and the size counter has nothing to show.
    this.timesliceMs = options.timesliceMs ?? 1000;
    this.now = options.now ?? ((): number => Date.now());
    this.onStatus = options.onStatus;
    this.state = 'READY';
  }

  /** Called by the host when a chunk arrives. */
  acceptChunk(chunk: RecordedChunk): void {
    // Chunks that arrive after a cancel are dropped rather than kept: the point
    // of cancelling is that no file results.
    if (this.cancelled) return;
    if (this.state !== 'RECORDING' && this.state !== 'STOPPING') return;
    if (chunk.size <= 0) return;
    this.chunks.push(chunk);
    this.byteCount += chunk.size;
    this.publish();
  }

  /**
   * Called by the host once the recorder has genuinely finished.
   *
   * This is what moves a recording to `DONE`, not `stop()`. The distinction is
   * §14: the final chunk arrives after the stop request, and a session that
   * declared itself finished at the request would drop the tail of the song.
   */
  acceptStop(): void {
    if (this.cancelled) {
      this.state = 'CANCELLED';
      this.release();
      this.publish();
      return;
    }
    if (this.state !== 'RECORDING' && this.state !== 'STOPPING') return;
    this.stoppedAt = this.now();
    if (this.chunks.length === 0 || this.byteCount === 0) {
      this.fail('no-data');
      return;
    }
    this.state = 'DONE';
    this.release();
    this.publish();
  }

  /** Called by the host when the recorder reports a failure. */
  acceptError(message: string): void {
    if (this.state === 'DONE' || this.state === 'CANCELLED') return;
    this.fail('recorder-error', message);
  }

  /**
   * Begins recording.
   *
   * Refuses from any state but `READY`, which is the whole of §9: a second
   * press while a recording is running must not start a second recorder writing
   * into the same session. Returns whether it started, so a caller can tell the
   * difference between "began" and "was already going".
   */
  start(options: { readonly automatic?: boolean } = {}): boolean {
    if (this.state !== 'READY') return false;
    this.automatic = options.automatic === true;
    this.cancelled = false;
    this.error = null;
    this.chunks.length = 0;
    this.byteCount = 0;
    try {
      this.recorder.start(this.timesliceMs);
    } catch (cause) {
      this.fail('start-failed', cause instanceof Error ? cause.message : String(cause));
      return false;
    }
    // Timed from after the recorder accepted, so the counter measures the
    // recording rather than the time spent asking for one.
    this.startedAt = this.now();
    this.state = 'RECORDING';
    this.publish();
    return true;
  }

  /**
   * Asks the recorder to finish.
   *
   * Moves to `STOPPING`, not `DONE`. See the note at the top of this file.
   */
  stop(): boolean {
    if (this.state !== 'RECORDING') return false;
    this.state = 'STOPPING';
    this.publish();
    try {
      this.recorder.stop();
    } catch (cause) {
      this.fail('recorder-error', cause instanceof Error ? cause.message : String(cause));
      return false;
    }
    return true;
  }

  /**
   * Abandons the recording.
   *
   * The chunks already collected are discarded and the state goes to
   * `CANCELLED` rather than `DONE`, so nothing downstream can offer a partial
   * capture as a finished file. The recorder is still stopped — a cancel that
   * left it running would keep the canvas capture alive for the rest of the
   * session.
   */
  cancel(): boolean {
    if (this.state !== 'RECORDING' && this.state !== 'STOPPING') return false;
    this.cancelled = true;
    this.chunks.length = 0;
    this.byteCount = 0;
    try {
      if (this.recorder.state !== 'inactive') this.recorder.stop();
      else {
        this.state = 'CANCELLED';
        this.release();
      }
    } catch {
      this.state = 'CANCELLED';
      this.release();
    }
    this.publish();
    return true;
  }

  /** Stops if running, then releases everything. Safe to call twice. */
  dispose(): void {
    if (this.state === 'RECORDING' || this.state === 'STOPPING') {
      this.cancelled = true;
      try {
        if (this.recorder.state !== 'inactive') this.recorder.stop();
      } catch {
        // Already stopped, or stopped by the error path.
      }
      this.state = 'CANCELLED';
    }
    this.release();
    this.recorder.dispose();
  }

  get status(): LiveRecordingStatus {
    return {
      state: this.state,
      elapsedSeconds: this.elapsed(),
      chunks: this.chunks.length,
      bytes: this.byteCount,
      mimeType: this.recorder.mimeType,
      hasAudio: this.source.hasAudio,
      automatic: this.automatic,
      error: this.error,
    };
  }

  /** The finished recording, or null until there is one. */
  get result(): LiveRecordingResult | null {
    if (this.state !== 'DONE') return null;
    return {
      chunks: [...this.chunks],
      bytes: this.byteCount,
      mimeType: this.recorder.mimeType,
      extension: this.extension,
      durationSeconds: this.elapsed(),
      width: this.source.width,
      height: this.source.height,
      fps: this.config.fps,
      hasAudio: this.source.hasAudio,
    };
  }

  get isRecording(): boolean {
    return this.state === 'RECORDING';
  }

  /** True while this recording belongs to the run rather than to a button. */
  get isAutomatic(): boolean {
    return this.automatic;
  }

  private elapsed(): number {
    if (this.startedAt === 0) return 0;
    const end = this.stoppedAt > 0 ? this.stoppedAt : this.now();
    return Math.max(0, (end - this.startedAt) / 1000);
  }

  private fail(kind: RecordingFailure, detail?: string): void {
    this.error = describeRecordingFailure(kind, detail);
    this.state = 'FAILED';
    this.release();
    this.publish();
  }

  /**
   * Lets go of the capture.
   *
   * Guarded, because it is reached from several endings and a canvas capture
   * track stopped twice throws on some engines. The tracks matter more than the
   * recorder: a live capture track keeps the compositor copying every frame for
   * as long as it exists, whether or not anything reads it.
   */
  private release(): void {
    if (this.released) return;
    this.released = true;
    try {
      this.source.release();
    } catch {
      // The capture is going away regardless.
    }
  }

  private publish(): void {
    this.onStatus?.(this.status);
  }
}
